import React from 'react';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useAppSelector } from '../../app/hooks';
import useLogout from '../../hooks/useLogout';

const initialsOf = (firstName?: string, lastName?: string, email?: string) => {
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.trim();
  if (initials) return initials.toUpperCase();
  return (email?.[0] ?? '?').toUpperCase();
};

const AvatarButton = styled(IconButton)({ padding: 0 });

// Matches the brand block's royal blue — deeper than action blue, lighter than the rail.
const InitialsAvatar = styled(Avatar)({
  width: 34,
  height: 34,
  fontSize: 13,
  fontWeight: 600,
  color: '#fff',
  backgroundColor: '#3868B9',
});

const MenuHeader = styled('div')({ padding: '12px 16px' });
const UserName = styled(Typography)({ fontSize: 14, fontWeight: 500, color: '#424242' });
const UserEmail = styled(Typography)({ fontSize: 12, color: '#595959', marginTop: 2 });

/**
 * Header avatar button + account dropdown. Relocated from the old drawer-bottom
 * "My Account" list. Session-expiry tracking now lives in the shell (SideNav),
 * so this component only handles the menu.
 */
export default function AccountMenu() {
  const { user } = useAppSelector((state) => state.user);
  const handleLogout = useLogout();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

  return (
    <>
      <AvatarButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label="User menu"
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
      >
        <InitialsAvatar>{initialsOf(user?.firstName, user?.lastName, user?.email)}</InitialsAvatar>
      </AvatarButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 268, borderRadius: '6px', mt: 1 } } }}
      >
        <MenuHeader>
          <UserName>{fullName}</UserName>
          <UserEmail>{user?.email}</UserEmail>
        </MenuHeader>
        <Divider />
        <MenuItem component={Link} to="/user-profile" onClick={() => setAnchorEl(null)}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>User Info</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            handleLogout();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sign Out</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
